import { EVENT_TYPE_LABELS, type SessionEvent } from '@/types';
import { getLiveEventCategoryLabel, getLiveEventZone, type LiveActionZone } from '@/components/live/LiveActionZones';
import { LIVE_PERIOD_LABELS, type LivePeriod } from '@/utils/liveClock';

export type JudgementStatKey = 'correct' | 'wrong' | 'uncertain' | 'missing';

export interface LiveEventStats {
  total: number;
  judgement: Record<JudgementStatKey, number>;
  category: Record<LiveActionZone | 'other', number>;
  byPeriod: Array<{ period: string; label: string; count: number }>;
  topEventTypes: Array<{ key: string; label: string; count: number }>;
}

function judgementKey(value?: string | null): JudgementStatKey {
  if (value === 'correct' || value === 'wrong' || value === 'uncertain') return value;
  return 'missing';
}

/** Same key as timeline cards: match time + period + judgement. */
export function liveEventGroupKey(event: SessionEvent): string {
  return `${event.match_second ?? 0}|${event.period ?? ''}|${event.judgement ?? ''}`;
}

const MARK_BATCH_MS = 2000;

/**
 * Group simultaneous multi-marks into one card/stat row.
 * Events share match time + period + judgement and were created within a short window.
 */
export function groupLiveEvents(events: SessionEvent[]): SessionEvent[][] {
  if (events.length === 0) return [];

  const sorted = [...events].sort(
    (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
  );

  const groups: SessionEvent[][] = [];
  for (const event of sorted) {
    const eventTime = new Date(event.created_at).getTime();
    const last = groups[groups.length - 1];
    const lastTime = last ? new Date(last[last.length - 1].created_at).getTime() : 0;
    if (
      last &&
      liveEventGroupKey(last[0]) === liveEventGroupKey(event) &&
      eventTime - lastTime <= MARK_BATCH_MS
    ) {
      last.push(event);
    } else {
      groups.push([event]);
    }
  }
  return groups;
}

export function computeLiveEventStats(events: SessionEvent[]): LiveEventStats {
  const groups = groupLiveEvents(events);

  const judgement: Record<JudgementStatKey, number> = {
    correct: 0,
    wrong: 0,
    uncertain: 0,
    missing: 0,
  };
  const category: Record<LiveActionZone | 'other', number> = {
    flow: 0,
    discipline: 0,
    technical: 0,
    other: 0,
  };
  const periodMap = new Map<string, number>();
  const typeMap = new Map<string, number>();

  for (const group of groups) {
    const head = group[0];
    judgement[judgementKey(head.judgement)] += 1;

    const period = head.period || 'unknown';
    periodMap.set(period, (periodMap.get(period) || 0) + 1);

    const zonesSeen = new Set<LiveActionZone | 'other'>();
    for (const event of group) {
      const zone = getLiveEventZone(event.event_type);
      zonesSeen.add(zone ?? 'other');
      typeMap.set(event.event_type, (typeMap.get(event.event_type) || 0) + 1);
    }
    for (const zone of zonesSeen) {
      category[zone] += 1;
    }
  }

  const byPeriod = [...periodMap.entries()]
    .map(([period, count]) => ({
      period,
      label: LIVE_PERIOD_LABELS[period as LivePeriod] || period,
      count,
    }))
    .sort((a, b) => b.count - a.count);

  const topEventTypes = [...typeMap.entries()]
    .map(([key, count]) => ({
      key,
      label: EVENT_TYPE_LABELS[key as keyof typeof EVENT_TYPE_LABELS] || key,
      count,
    }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 12);

  return { total: groups.length, judgement, category, byPeriod, topEventTypes };
}

export const JUDGEMENT_STAT_LABELS: Record<JudgementStatKey, string> = {
  correct: '正確',
  wrong: '錯誤',
  uncertain: '灰色',
  missing: 'Missing',
};

export const JUDGEMENT_STAT_COLORS: Record<JudgementStatKey, string> = {
  correct: '#2e7d32',
  wrong: '#c62828',
  uncertain: '#546e7a',
  missing: '#ef6c00',
};

export const CATEGORY_STAT_LABELS: Record<LiveActionZone | 'other', string> = {
  flow: '球判決',
  discipline: '判罰',
  technical: '犯規事件',
  other: '其他',
};

export function getEventCategoryLabel(eventType: string): string {
  return getLiveEventCategoryLabel(eventType);
}
